import { type ReactNode, useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import {
    FormNumberField,
    FormSelect,
    FormTextarea,
    FormTextField,
} from '@/components/organisms/FishGroupForms';
import { isStockingCycleNameTaken, suggestStockingCycleName } from '@/domain/stockingCycles';
import { now } from '@/utils/date';

import ModalFormActions from '../ModalFormActions/ModalFormActions';

import {
    type NewStockingFormValues,
    parseWeek,
    parseYear,
    weekContradictsSeason,
} from './newStockingForm';

import styles from './NewStockingModal.module.scss';

interface StockingFormProps {
    defaults: NewStockingFormValues;
    /**
     * The name the field starts following season and year with: it keeps following while it
     * still equals the suggestion. Null when the starting name was the user's own.
     */
    followName: string | null;
    /** Names the stocking can't take: the site's other stockings. */
    takenNames: readonly string[];
    /** Edit shows the stocking's notes; New stocking has none. */
    withNotes?: boolean;
    /** Edit: the stocking's own year, accepted even outside New stocking's window. */
    ownYear?: number | null;
    submitLabel: string;
    /** Below the fields: what the dialog wants the user to know. */
    note: ReactNode;
    /** A non-blocking warning for the values as they stand. */
    warning?: (values: NewStockingFormValues) => ReactNode;
    serverError: ReactNode;
    onCancel: () => void;
    onSubmit: (values: NewStockingFormValues) => Promise<void>;
}

/**
 * The New stocking form: season, year, planned week and name (and notes when editing), with
 * the name following season and year until the user types their own.
 */
export default function StockingForm(props: StockingFormProps) {
    const { defaults, followName, takenNames, withNotes = false, ownYear = null } = props;
    const { submitLabel } = props;
    const { note, warning, serverError, onCancel, onSubmit } = props;
    const { t } = useTranslation('fishGroups');
    const { handleSubmit, control, setValue, getValues, formState } =
        useForm<NewStockingFormValues>({ defaultValues: defaults });
    const values = useWatch({ control });

    // The name follows season and year until the customer types their own. The starting
    // name stays while its season and year do. The field's dirty flag can't tell the two
    // apart: a name set here counts as dirty too.
    const autoName = useRef(followName);
    const season = values.season;
    const year = parseYear(values.year ?? '', now(), ownYear);
    useEffect(() => {
        if (!season || year === null || getValues('name') !== autoName.current) return;
        const prefilled = season === defaults.season && String(year) === defaults.year;
        autoName.current = prefilled
            ? defaults.name
            : suggestStockingCycleName(season, year, takenNames);
        setValue('name', autoName.current, { shouldValidate: true });
    }, [season, year, takenNames, defaults, getValues, setValue]);

    const current: NewStockingFormValues = {
        season: values.season ?? defaults.season,
        year: values.year ?? '',
        week: values.week ?? '',
        name: values.name ?? '',
        notes: values.notes ?? '',
    };
    const showSeasonHint = weekContradictsSeason(current, now(), ownYear);
    const warningNode = warning?.(current);

    return (
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.grid}>
                <FormSelect
                    control={control}
                    name="season"
                    label={t('newStocking.fields.season')}
                    options={[
                        { value: 'spring', label: t('newStocking.seasons.spring') },
                        { value: 'autumn', label: t('newStocking.seasons.autumn') },
                    ]}
                    rules={{ required: true }}
                />
                <FormNumberField
                    control={control}
                    name="year"
                    label={t('newStocking.fields.year')}
                    rules={{
                        validate: (value) =>
                            parseYear(value, now(), ownYear) !== null ||
                            t('newStocking.errors.year'),
                    }}
                />
                <FormNumberField
                    control={control}
                    name="week"
                    label={t('newStocking.fields.week')}
                    min={1}
                    max={53}
                    rules={{
                        validate: (value, all) =>
                            parseWeek(value, parseYear(all.year, now(), ownYear)) !== null ||
                            t('newStocking.errors.week'),
                    }}
                />
                <FormTextField
                    control={control}
                    name="name"
                    label={t('newStocking.fields.name')}
                    rules={{
                        validate: (value) =>
                            value.trim() === ''
                                ? t('actions.errors.required')
                                : !isStockingCycleNameTaken(value, takenNames) ||
                                  t('actions.errors.codes.stockingCycleNameTaken'),
                    }}
                />
            </div>
            {withNotes && (
                <FormTextarea
                    control={control}
                    name="notes"
                    label={t('editStocking.fields.notes')}
                    optional
                />
            )}
            {showSeasonHint && (
                // Advice while typing, so it waits for a pause rather than interrupting.
                <Alert tone="warning" size="small" live="polite">
                    {t('newStocking.seasonHint')}
                </Alert>
            )}
            {warningNode}
            <Alert tone="neutral" size="small">
                {note}
            </Alert>
            {serverError && <p className={styles.serverError}>{serverError}</p>}
            <ModalFormActions
                cancelLabel={t('actions.cancel')}
                submitLabel={submitLabel}
                isSubmitting={formState.isSubmitting}
                onCancel={onCancel}
            />
        </form>
    );
}
